import { useState, useEffect } from 'react';
import { Navigate } from 'react-router-dom';
import { usersAPI } from '../utils/api';
import { useAuth } from '../hooks/useAuth';
import { PageHeader, Modal, FormField, SelectField, EmptyState } from '../components/layout/UI';
import { ALL_ROLES, ROLE_COLOR, getPermLevel, isAdminRole } from '../utils/rolesConfig';
import { useToast, apiError } from '../components/Toast';
import { Lock, Plus, Search, SquarePen, UserCheck, UserX, Users } from 'lucide-react';

const P = () => <Plus size={14} />;
const Pen = () => <SquarePen size={13} />;

const ROLE_OPTS = ALL_ROLES.map(r => ({ value:r.value, label:r.label }));
// Colour comes from rolesConfig so every one of the 16 roles renders a badge.
const roleStyle = (role) => {
  const c = ROLE_COLOR[role] || '#64748b';
  return { background:`${c}1a`, color:c };
};

export default function UsersPage() {
  const toast = useToast();
  const { user: me } = useAuth();
  const isAdmin = isAdminRole(me?.role);

  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [showModal, setShowModal] = useState(false);
  const [editUser, setEditUser] = useState(null);
  const [form, setForm] = useState({ name:'', email:'', role:'Specialist', password:'' });
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [confirmToggle, setConfirmToggle] = useState(null);
  const [showPwModal, setShowPwModal] = useState(false);
  const [pwTarget, setPwTarget] = useState(null);
  const [pwForm, setPwForm] = useState({ current_password:'', new_password:'', confirm:'' });
  const [pwSaving, setPwSaving] = useState(false);
  const [pwError, setPwError] = useState('');

  const load = async () => { try { const r = await usersAPI.getAll(); setUsers(r.data); } catch(e) { toast.error(apiError(e, 'Something went wrong loading this.')); } finally{setLoading(false);} };
  useEffect(()=>{load();},[]);

  // Guard runs AFTER every hook so hook order stays stable across renders.
  if (['Specialist','Developer'].includes(getPermLevel(me?.role))) return <Navigate to="/" />;

  const filtered = users.filter(u => !search || u.name.toLowerCase().includes(search.toLowerCase()) || u.email.toLowerCase().includes(search.toLowerCase()));

  const openCreate = () => { setEditUser(null); setForm({ name:'', email:'', role:'Specialist', password:'' }); setError(''); setShowModal(true); };
  const openEdit = (u) => { setEditUser(u); setForm({ name:u.name, email:u.email, role:u.role, password:'' }); setError(''); setShowModal(true); };

  const handleSave = async () => {
    if (!form.name||!form.email) return setError('Name and email required');
    setSaving(true);
    try {
      // No password: the account is created with an unguessable one and the
      // person sets their own from an emailed link.
      const payload = { name:form.name, email:form.email, role:form.role };
      editUser ? await usersAPI.update(editUser.id,payload) : await usersAPI.create(payload);
      toast.success(editUser ? 'User updated' : 'User created');
      setShowModal(false); load();
    } catch(e){setError(e.response?.data?.message||'Save failed');} finally{setSaving(false);}
  };

  const handleToggle = async () => {
    if (!confirmToggle) return;
    const activating = !confirmToggle.is_active;
    try {
      await usersAPI.update(confirmToggle.id, { is_active: activating });
      toast.success(`${confirmToggle.name} ${activating ? 'activated' : 'deactivated'}`);
    } catch(e) { toast.error(apiError(e, 'Could not change that account.')); }
    finally { setConfirmToggle(null); load(); }
  };

  const handlePwChange = async () => {
    if (!pwForm.new_password) return setPwError('New password required');
    if (pwForm.new_password.length<8) return setPwError('Minimum 8 characters');
    if (pwForm.new_password!==pwForm.confirm) return setPwError('Passwords do not match');
    setPwSaving(true);
    try { await usersAPI.changePassword(pwTarget.id, pwForm.current_password, pwForm.new_password);
           toast.success('Password updated'); setShowPwModal(false); }
    catch(e){setPwError(e.response?.data?.message||'Failed');} finally{setPwSaving(false);}
  };

  return (
    <div>
      <PageHeader title="Users" subtitle="Manage team members and access"
        actions={isAdmin&&<button onClick={openCreate} className="btn-primary flex items-center gap-1.5 text-sm"><P /> Add User</button>} />

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
        {[{l:'Total',v:users.length},{l:'Active',v:users.filter(u=>u.is_active).length},{l:'Admins',v:users.filter(u=>isAdminRole(u.role)).length},{l:'Managers',v:users.filter(u=>getPermLevel(u.role)==='Manager').length}].map(s=>(
          <div key={s.l} className="stat-card"><span className="stat-label">{s.l}</span><p className="text-3xl font-bold" style={{ color:'var(--text)' }}>{s.v}</p></div>
        ))}
      </div>

      <div className="card">
        <div className="relative mb-4 max-w-sm">
          <span className="absolute left-3 top-1/2 -translate-y-1/2" style={{ color:'var(--text-secondary)' }}><Search size={15}/></span>
          <input className="input pl-9 text-sm" placeholder="Search users..." value={search} onChange={e=>setSearch(e.target.value)} />
        </div>
        {loading ? <div className="flex justify-center py-12"><div className="w-6 h-6 border-2 border-brand-500 border-t-transparent rounded-full animate-spin" /></div>
        : filtered.length===0 ? <EmptyState icon={Users} title="No users found" />
        : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead><tr className="border-b" style={{ borderColor:'var(--border)' }}>
                {['User','Email','Role','Status','Joined','Actions'].map(h=>(
                  <th key={h} className="text-left py-2 px-3 text-xs font-semibold uppercase tracking-wider" style={{ color:'var(--text-secondary)' }}>{h}</th>
                ))}
              </tr></thead>
              <tbody>
                {filtered.map(u=>(
                  <tr key={u.id} className="border-b hover:bg-[var(--surface-alt)] transition-colors" style={{ borderColor:'var(--border)' }}>
                    <td className="py-3 px-3">
                      <div className="flex items-center gap-2">
                        <div className="w-8 h-8 rounded-lg flex items-center justify-center font-bold text-white text-xs" style={{ background:u.avatar_color||'#234180' }}>{u.name?.split(' ').map(n=>n[0]).join('').slice(0,2).toUpperCase()}</div>
                        <div><p className="font-medium" style={{ color:'var(--text)' }}>{u.name}</p>{u.id===me?.id&&<span className="text-xs text-brand-500">You</span>}</div>
                      </div>
                    </td>
                    <td className="py-3 px-3 text-xs" style={{ color:'var(--text-secondary)' }}>{u.email}</td>
                    <td className="py-3 px-3"><span className="badge" style={roleStyle(u.role)}>{u.role}</span></td>
                    <td className="py-3 px-3"><span className={`badge ${u.is_active?'bg-emerald-100 text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-400':'bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-400'}`}>{u.is_active?'Active':'Inactive'}</span></td>
                    <td className="py-3 px-3 text-xs" style={{ color:'var(--text-secondary)' }}>{u.created_at?new Date(u.created_at).toLocaleDateString():'—'}</td>
                    <td className="py-3 px-3">
                      <div className="flex gap-1">
                        {(isAdmin||u.id===me?.id)&&<button onClick={()=>openEdit(u)} className="p-1.5 rounded hover:bg-[var(--surface-alt)]" style={{ color:'var(--text-secondary)' }}><Pen /></button>}
                        <button onClick={()=>{setPwTarget(u);setPwForm({current_password:'',new_password:'',confirm:''});setPwError('');setShowPwModal(true);}} className="p-1.5 rounded hover:bg-[var(--surface-alt)]" style={{ color:'var(--text-secondary)' }} disabled={!isAdmin&&u.id!==me?.id}><Lock size={13}/></button>
                        {isAdmin&&u.id!==me?.id&&<button onClick={()=>setConfirmToggle(u)} className={`p-1.5 rounded transition-colors ${u.is_active?'hover:text-red-500':'hover:text-emerald-500'}`}>{u.is_active?<UserX size={13}/>:<UserCheck size={13}/>}</button>}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      <Modal isOpen={showModal} onClose={()=>setShowModal(false)} title={editUser?'Edit User':'Add User'} size="md">
        <div className="space-y-3">
          {error&&<div className="p-3 rounded-lg bg-red-50 border border-red-200 text-red-600 text-sm dark:bg-red-900/20 dark:border-red-800 dark:text-red-400">{error}</div>}
          <FormField label="Full Name"><input className="input" value={form.name} onChange={e=>setForm(f=>({...f,name:e.target.value}))} /></FormField>
          <FormField label="Email"><input type="email" className="input" value={form.email} onChange={e=>setForm(f=>({...f,email:e.target.value}))} /></FormField>
          {isAdmin&&<FormField label="Role"><SelectField value={form.role} onChange={e=>setForm(f=>({...f,role:e.target.value}))} options={ROLE_OPTS} placeholder="" /></FormField>}
          {!editUser&&<p className="text-xs" style={{ color:'var(--text-secondary)' }}>The user will receive an email with a link to set their own password.</p>}
          <div className="flex justify-end gap-3 pt-2">
            <button onClick={()=>setShowModal(false)} className="btn-secondary">Cancel</button>
            <button onClick={handleSave} disabled={saving} className="btn-primary">{saving?'Saving...':(editUser?'Save Changes':'Create User')}</button>
          </div>
        </div>
      </Modal>

      <Modal isOpen={showPwModal} onClose={()=>setShowPwModal(false)} title={`Change Password — ${pwTarget?.name||''}`} size="sm">
        <div className="space-y-3">
          {pwError&&<div className="p-3 rounded-lg bg-red-50 border border-red-200 text-red-600 text-sm">{pwError}</div>}
          {pwTarget?.id===me?.id&&<FormField label="Current Password"><input type="password" className="input" value={pwForm.current_password} onChange={e=>setPwForm(f=>({...f,current_password:e.target.value}))} /></FormField>}
          <FormField label="New Password"><input type="password" className="input" value={pwForm.new_password} onChange={e=>setPwForm(f=>({...f,new_password:e.target.value}))} placeholder="Min 8 characters" /></FormField>
          <FormField label="Confirm Password"><input type="password" className="input" value={pwForm.confirm} onChange={e=>setPwForm(f=>({...f,confirm:e.target.value}))} /></FormField>
          <div className="flex justify-end gap-3 pt-2">
            <button onClick={()=>setShowPwModal(false)} className="btn-secondary">Cancel</button>
            <button onClick={handlePwChange} disabled={pwSaving} className="btn-primary">{pwSaving?'Saving...':'Update Password'}</button>
          </div>
        </div>
      </Modal>

      <Modal isOpen={!!confirmToggle} onClose={()=>setConfirmToggle(null)} title={confirmToggle?.is_active?'Deactivate User':'Activate User'} size="sm">
        <p className="text-sm mb-4" style={{ color:'var(--text-secondary)' }}>{confirmToggle?.is_active?`Deactivating ${confirmToggle?.name} will prevent them from logging in.`:`This will restore ${confirmToggle?.name}'s access.`}</p>
        <div className="flex justify-end gap-3">
          <button onClick={()=>setConfirmToggle(null)} className="btn-secondary">Cancel</button>
          <button onClick={handleToggle} className={confirmToggle?.is_active?'btn-danger':'btn-primary'}>{confirmToggle?.is_active?'Deactivate':'Activate'}</button>
        </div>
      </Modal>
    </div>
  );
}
